import React, { useState, useEffect, useContext } from 'react';
import { Box, Stack, Paper, Typography, Table, TableBody, TableContainer, TableHead, TableRow, IconButton } from '@mui/material';
import Grid from '@mui/material/Grid';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import { AccordionSummary, AccordionDetails, Accordion } from '../shared/components/accordion';
import StyledTableRow from '../shared/components/styledTableRow';
import StyledTableCell from '../shared/components/styledTableCell';
import ConfirmDialog from '../shared/components/confirmDialog';
import { FormMode } from '../utils/extensions/constantEx'
import { ServicePaymentAPIUrl } from '../utils/extensions/functionEx'
import { HomeContext } from '../utils/contexts/homeContext';
import { ServiceFormContext } from '../utils/contexts/serviceFormContext';
import { GetDayJSValueFromUTCDate, GetNoToCurrencyFormat, PatientServicePaymentAPIUrl } from '../utils/extensions/functionEx';
import { DATE_DISPLAY_FORMAT } from '../startups/apiConfig';
import PaymentForm from './paymentForm';
import { useCookies } from 'react-cookie';

const ServicePaymentView = (props) => {
    const { expanded, formOpenMode, serviceId, handleAccordionChange } = props;
    const { ShowAPIError, OpenErrorAlert, OpenSuccessAlert, homeState, ClearLoggedUser } = useContext(HomeContext);
    const { selectedPatientService, isPaymentFormOpen, SetIsShowPaymentForm, SetPageLoading } = useContext(ServiceFormContext);

    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
    const [selectedPayment, setSelectedPayment] = useState(null);
    const [patientPayments, setPatientPayments] = useState([]);

    const [cookies, setCookie] = useCookies(['hplus_home_care_user_session_code']);

    useEffect(() => {
        fetchServicePayments();
        setSelectedPayment(null);
        setIsDeleteConfirmOpen(false);
    }, [selectedPatientService]);

    async function fetchServicePayments() {
        try {
            const headers = new Headers();
            headers.append("Hc-App-Auth-Token", cookies.hplus_home_care_user_session_code);

            const response = await fetch(ServicePaymentAPIUrl(homeState.loggedUser.companyCode, selectedPatientService.service_id), {
                method: "GET",
                headers: headers
            });
            if (response.status === 401) {
                OpenErrorAlert('User login history expired, please log in again.');
                setCookie('hplus_home_care_user_session_code', '', { path: '/' });
                ClearLoggedUser();
            } else if (response.status === 200) {
                setPatientPayments(await response.json());
            } else {
                ShowAPIError(response)
                setPatientPayments([]);
            }
        }
        catch (error) {
            OpenErrorAlert(error);
            setPatientPayments([]);
        }
    }

    const handleDeleteClick = (payment) => {
        setIsDeleteConfirmOpen(true);
        setSelectedPayment(payment);
    }

    const handleDeleteCancelClick = () => {
        setIsDeleteConfirmOpen(false);
        setSelectedPayment(null);
    }

    const handleDeleteOKClick = async () => {
        try {
            if (!selectedPayment || selectedPayment.id <= 0) {
                OpenErrorAlert('Enter valid payment details.')
                return;
            }
            SetPageLoading(true);

            const headers = new Headers();
            headers.append("Hc-App-Auth-Token", cookies.hplus_home_care_user_session_code);
            const response = await fetch(PatientServicePaymentAPIUrl(homeState.loggedUser.companyCode, selectedPayment.id), {
                method: "GET",
                headers: headers
            });
            if (response.status === 401) {
                OpenErrorAlert('User login history expired, please log in again.');
                setCookie('hplus_home_care_user_session_code', '', { path: '/' });
                ClearLoggedUser();
            } else if (response.status === 200) {
                const tempPayment = await response.json();
                if (tempPayment) {
                    const headers = new Headers();
                    headers.append("Accept", "application/json");
                    headers.append("Accept-Encoding", "application/json");
                    headers.append("Content-Type", "application/json");
                    headers.append("Hc-App-Auth-Token", cookies.hplus_home_care_user_session_code);

                    const tempReqModel = {
                        service_pay_id: selectedPayment.id,
                        service_pay_doc_id: 0,
                        doc_id: 0
                    };

                    if (tempPayment.documents && tempPayment.documents.length > 0) {
                        tempReqModel.doc_id = tempPayment.documents[0].document_id;
                        tempReqModel.service_pay_doc_id = tempPayment.documents[0].id;
                    }

                    const responseDel = await fetch(PatientServicePaymentAPIUrl(homeState.loggedUser.companyCode), {
                        method: "DELETE",
                        headers: headers,
                        body: JSON.stringify(tempReqModel)
                    })
                    if (response.status === 401) {
                        OpenErrorAlert('User login history expired, please log in again.');
                        setCookie('hplus_home_care_user_session_code', '', { path: '/' });
                        ClearLoggedUser();
                    } else if (responseDel.status === 200) {
                        OpenSuccessAlert('Patient payment deleted successfully.');
                        await fetchServicePayments();
                    } else {
                        ShowAPIError(responseDel);
                    }
                }
            } else {
                ShowAPIError(response);
            }
        }
        catch (error) {
            OpenErrorAlert(error)
        }
        finally {
            setIsDeleteConfirmOpen(false);
            setSelectedPayment(null);
            SetPageLoading(false);
        }
    }

    const handleEditClick = async (payment) => {
        try {
            if (!payment || payment.id <= 0) {
                OpenErrorAlert('Select valid payment details.')
                return;
            }
            SetPageLoading(true);

            const headers = new Headers();
            headers.append("Hc-App-Auth-Token", cookies.hplus_home_care_user_session_code);
            const response = await fetch(PatientServicePaymentAPIUrl(homeState.loggedUser.companyCode, payment.id), {
                method: "GET",
                headers: headers
            });

            if (response.status === 401) {
                OpenErrorAlert('User login history expired, please log in again.');
                setCookie('hplus_home_care_user_session_code', '', { path: '/' });
                ClearLoggedUser();
            } else if (response.status === 200) {
                const tempPayment = await response.json();
                if (tempPayment) {
                    SetIsShowPaymentForm(true);
                    setSelectedPayment(tempPayment);
                }
            } else {
                ShowAPIError(response);
            }
        }
        catch (error) {
            OpenErrorAlert(error)
        }
        finally {
            SetPageLoading(false);
        }
    }

    const handlePaymentFormCloseClick = async () => {
        SetIsShowPaymentForm(false);
        await fetchServicePayments();
    }

    return (

        <Accordion expanded={expanded === 'panelPatientPayment'} onChange={handleAccordionChange('panelPatientPayment')}>
            <AccordionSummary id="panel-patient-payments">
                <Typography>Patient Payments</Typography>
            </AccordionSummary>
            <AccordionDetails>
                <Stack spacing={1}>
                    <Grid container spacing={1}>
                        <Grid size={12}>
                            <Box sx={{ pr: 1, float: 'right' }}>
                                {
                                    isDeleteConfirmOpen &&
                                    <ConfirmDialog isOpen={isDeleteConfirmOpen} title="Confirm Delete"
                                        message="Are you sure you want to delete the payment details?"
                                        handleClickOK={handleDeleteOKClick}
                                        handleClickCancel={handleDeleteCancelClick} />

                                }
                                {
                                    isPaymentFormOpen &&
                                    <PaymentForm selectedPayment={selectedPayment} handlePaymentFormCloseClick={handlePaymentFormCloseClick} />
                                }
                            </Box>
                        </Grid>
                        <Grid size={12}>
                            <TableContainer component={Paper} sx={{ maxHeight: '77vh' }}>
                                <Table stickyHeader sx={{ minWidth: 700 }}>
                                    <TableHead>
                                        <TableRow>
                                            <StyledTableCell sx={{ width: '250px' }}>Receipt No</StyledTableCell>
                                            <StyledTableCell sx={{ width: '250px' }}>Pay Date</StyledTableCell>
                                            <StyledTableCell sx={{ width: '250px' }}>Amount</StyledTableCell>
                                            <StyledTableCell>Remarks</StyledTableCell>
                                            <StyledTableCell sx={{ width: '90px' }}>&nbsp;</StyledTableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {patientPayments.map((row) => (
                                            <StyledTableRow key={row.id}>
                                                <StyledTableCell component="th" scope="row">
                                                    {row.receipt_no}
                                                </StyledTableCell>
                                                <StyledTableCell>{GetDayJSValueFromUTCDate(row.pay_date).format(DATE_DISPLAY_FORMAT)}</StyledTableCell>
                                                <StyledTableCell>{GetNoToCurrencyFormat(row.amount)}</StyledTableCell>
                                                <StyledTableCell>{row.remarks}</StyledTableCell>
                                                <StyledTableCell>
                                                    {
                                                        formOpenMode !== FormMode.ViewMode &&
                                                        <Stack direction="row" alignItems="center" spacing={1}>
                                                            <IconButton size="small" onClick={() => handleEditClick(row)}>
                                                                <EditIcon fontSize="inherit" />
                                                            </IconButton>
                                                            <IconButton size="small" onClick={() => handleDeleteClick(row)}>
                                                                <RemoveCircleOutlineIcon fontSize="small" />
                                                            </IconButton>
                                                        </Stack>
                                                    }
                                                </StyledTableCell>
                                            </StyledTableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Grid>
                    </Grid>
                </Stack>
            </AccordionDetails>
        </Accordion>

    );
}
export default ServicePaymentView;